<template>
  <div data-component-group="form" data-component="edit-package">
    <PackageForm
      :package-item="packageItem"
      confirm-trigger-label="Update package"
      :is-disabled="!canEditDetails"
      :is-loading="isLoading"
      :on-submit="onSubmit"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Details"
          :has-trigger="hasTrigger"
          :trigger-label="triggerLabel"
          @toggle="toggleEditDetails"
        />
      </template>
    </PackageForm>

    <LabelsForm
      :package-item="packageItem"
      :labels="labels"
      :is-disabled="!canEditLabels"
      :on-submit="onUpdateLabels"
      :on-remove="onRemoveLabels"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Labels"
          :trigger-label="!canEditLabels ? 'Edit' : 'Close'"
          @toggle="toggleLabels"
        />
      </template>
    </LabelsForm>

    <PackageSubmissionLimitsForm
      :package-item="packageItem"
      :limits="packageSubmissionLimits"
      :is-disabled="!canEditPackageSubmissionLimits"
      :on-submit="onUpdatePackageSubmissionLimit"
      :on-remove="onRemovePackageSubmissionLimit"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Package submission limits"
          :trigger-label="!canEditPackageSubmissionLimits ? 'Edit' : 'Close'"
          @toggle="togglePackageSubmissionLimits"
        />
      </template>
    </PackageSubmissionLimitsForm>

    <QueuesForm
      :package-item="packageItem"
      :queues="queues"
      :is-disabled="!canEditQueues"
      :on-submit="onUpdateQueue"
      :on-remove="onRemoveQueue"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Queues"
          :trigger-label="!canEditQueues ? 'Edit' : 'Close'"
          @toggle="toggleQueues"
        />
      </template>
    </QueuesForm>
  </div>
</template>

<script>
  import PackageForm from './AddNew';
  import LabelsForm from './Labels';
  import PackageSubmissionLimitsForm from './PackageSubmissionLimits';
  import QueuesForm from './Queues';
  import defaultPagination from '../../../config/pagination';
  import { isObj } from '../../../utils';

  // TODO: Translate component

  export default {
    components: {
      PackageForm,
      LabelsForm,
      PackageSubmissionLimitsForm,
      QueuesForm,
    },
    props: {
      packageItem: {
        type: Object,
        required: true,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        default: undefined,
      },
    },
    data() {
      return {
        canEditDetails: false,
        canEditLabels: false,
        canEditPackageSubmissionLimits: false,
        canEditQueues: false,
      };
    },
    computed: {
      triggerLabel() {
        return !this.canEditDetails ? 'Edit details' : 'Close';
      },
      hasTrigger() {
        return this.$ability.can('update', 'package');
      },
      labels() {
        const { labels } = this.$store.getters['packages/activeItem'];
        return labels;
      },
      packageSubmissionLimits() {
        const { packageSubmissionLimits } =
          this.$store.getters['packages/activeItem'];
        return packageSubmissionLimits;
      },
      queues() {
        const { queues } = this.$store.getters['packages/activeItem'];
        return queues;
      },
    },
    created() {
      if (this.packageItem?.id) {
        this.getLabels(this.packageItem);
        this.getPackageSubmissionsLimits(this.packageItem);
        this.getQueues(this.packageItem);
      }
    },
    watch: {
      'packageItem.id'(nextId) {
        if (nextId) {
          const nextPackageItem = this.packageItem;
          this.pagination = defaultPagination;
          this.canEditDetails = false;
          this.canEditLabels = false;
          this.canEditPackageSubmissionLimits = false;
          this.canEditQueues = false;

          this.getLabels(nextPackageItem);
          this.getPackageSubmissionsLimits(nextPackageItem);
          this.getQueues(nextPackageItem);
        }
      },
    },
    methods: {
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      toggleLabels() {
        this.canEditLabels = !this.canEditLabels;
      },
      togglePackageSubmissionLimits() {
        this.canEditPackageSubmissionLimits =
          !this.canEditPackageSubmissionLimits;
      },
      toggleQueues() {
        this.canEditQueues = !this.canEditQueues;
      },
      getLabels({ id }) {
        const query = { id };
        return this.$store.dispatch('packages/getLabels', query);
      },
      async onUpdateLabels({ labels, country }) {
        const query = {
          id: this.packageItem.id,
          labels,
          country: country.code,
        };

        return this.$store.dispatch('packages/addLabels', query);
      },
      onRemoveLabels(item) {
        const query = {
          id: this.packageItem.id,
          country: item.country.code,
        };

        return this.$store.dispatch('packages/removeLabels', query);
      },
      getPackageSubmissionsLimits({ id }) {
        const query = { id };
        return this.$store.dispatch(
          'packages/getPackageSubmissionsLimits',
          query,
        );
      },
      onUpdatePackageSubmissionLimit({ country, weekly, daily }) {
        const query = {
          id: this.packageItem.id,
          country: country.code,
          max_deals_per_week: weekly,
          max_deals_per_day: daily,
        };

        return this.$store.dispatch('packages/addPackageSubmissionLimit', query);
      },
      onRemovePackageSubmissionLimit(item) {
        const query = {
          id: this.packageItem.id,
          country: item.country.code,
        };

        return this.$store.dispatch(
          'packages/removePackageSubmissionLimit',
          query,
        );
      },
      getQueues({ id }) {
        const query = { id };
        return this.$store.dispatch('packages/getQueues', query);
      },
      onUpdateQueue({ queue, country }) {
        const query = {
          id: this.packageItem.id,
          queue,
          country: country.code,
        };

        return this.$store.dispatch('packages/addQueue', query);
      },
      onRemoveQueue(item) {
        const query = {
          id: this.packageItem.id,
          country: item.country.code,
        };

        return this.$store.dispatch('packages/removeQueue', query);
      },
    },
  };
</script>
